import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth, useHasAnyRole } from './hooks';
import type { AppRole } from './roles';
import { ForbiddenPage } from '@/app/pages/ForbiddenPage';
import {
  useCanAccessPage,
  usePagePermissionsReady,
  usePagePermissionsStore,
} from './page-permissions';
import type { RouteKey } from './rbac';

function FullPageSpinner() {
  return (
    <div className="flex h-dvh w-full items-center justify-center text-muted-foreground">
      Loading…
    </div>
  );
}

/**
 * Renders `children ?? <Outlet/>` so it works BOTH as a layout-route element and as a
 * children-wrapper (CONTRACTS.md §9).
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status, isAuthenticated } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />; // avoid login flash during hydration
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children ?? <Outlet />}</>;
}

/** 403 (not a redirect) when authenticated-but-wrong-role. Same dual-usage contract (CONTRACTS.md §9). */
export function RequireRole({ roles, children }: { roles: AppRole[]; children?: ReactNode }) {
  const allowed = useHasAnyRole(roles);
  if (!allowed) return <ForbiddenPage />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Gate by System & Permissions page key (effective-permissions). Falls back to
 * {@link RequireRole} seed while permissions are still loading.
 */
export function RequirePage({
  page,
  fallbackRoles,
  children,
}: {
  page: RouteKey | string;
  fallbackRoles: readonly AppRole[];
  children?: ReactNode;
}) {
  const ready = usePagePermissionsReady();
  const byPage = useCanAccessPage(page);
  const byRole = useHasAnyRole([...fallbackRoles]);
  const status = usePagePermissionsStore((s) => s.status);

  if (!ready && status === 'loading') return <FullPageSpinner />;
  if (ready ? !byPage : !byRole) return <ForbiddenPage />;
  return <>{children ?? <Outlet />}</>;
}
